import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { LuExternalLink, LuFileText } from "react-icons/lu";
import SiteModal from "../SiteModal";
import Button from "../elements/button";
import { toSafeHttpsUrl } from "./consentUtils";

const FALLBACK_AFTER_MS = 8000;

interface Props {
  title: string | null;
  url: string | null;
  onClose: () => void;
}

/** Frame + load/timeout hint. Remounts on every open, so state starts fresh. */
const ViewerFrame: React.FC<{ title: string; url: string | null }> = ({ title, url }) => {
  const { t } = useTranslation("consent");
  const [loaded, setLoaded] = useState(false);
  const [timedOut, setTimedOut] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setTimedOut(true), FALLBACK_AFTER_MS);
    return () => clearTimeout(timer);
  }, []);

  if (!url) return <p className="p-6 text-base text-gray-500">{t("viewer.cantDisplay")}</p>;
  return (
    <>
      <iframe
        title={title}
        src={url}
        className="w-full h-full border-0"
        sandbox="allow-scripts allow-same-origin allow-popups"
        referrerPolicy="no-referrer"
        onLoad={() => setLoaded(true)}
      />
      {!loaded && (
        <div className="absolute inset-0 flex flex-col gap-3 p-6 bg-white pointer-events-none">
          <div className="h-8 w-36 rounded bg-gray-100 animate-pulse" />
          <div className="h-7 w-3/5 mt-4 rounded bg-gray-100 animate-pulse" />
          <div className="h-3.5 rounded bg-gray-100 animate-pulse" />
          <div className="h-3.5 rounded bg-gray-100 animate-pulse" />
          <div className="h-3.5 w-5/6 rounded bg-gray-100 animate-pulse" />
          {timedOut && <p className="mt-auto text-sm text-gray-600">{t("viewer.notShowing")}</p>}
        </div>
      )}
    </>
  );
};

/**
 * In-app viewer over the consent window. Form state lives in the parent, so
 * closing returns to the window with every selection intact.
 */
const DocumentViewer: React.FC<Props> = ({ title, url: rawUrl, onClose }) => {
  const { t } = useTranslation("consent");
  const url = toSafeHttpsUrl(rawUrl);

  return (
    <SiteModal
      isOpen={!!title}
      onClose={onClose}
      width
      modalWidth={1}
      closeOnBackdrop={false}
      title={
        <span className="flex items-center gap-3 min-w-0">
          <span className="flex items-center justify-center w-8 h-8 rounded-lg bg-white/20 shrink-0">
            <LuFileText size={16} />
          </span>
          <span className="min-w-0">
            <span className="block text-lg font-semibold truncate">{title}</span>
            <span className="block text-sm font-normal opacity-80 truncate">{url ?? t("viewer.unavailable")}</span>
          </span>
        </span>
      }
    >
      <div className="relative bg-white" style={{ height: "62vh" }}>
        <ViewerFrame key={url ?? "none"} title={title ?? ""} url={url} />
      </div>
      <div className="flex items-center justify-between gap-3 px-4 py-3 border-t border-gray-200">
        <span className="text-sm text-gray-500">
          {t("viewer.loadedFrom")}
          {url && (
            <>
              {" · "}
              <a
                href={url}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 font-medium text-blue-600 hover:underline"
              >
                {t("viewer.openNewTab")} <LuExternalLink size={12} />
              </a>
            </>
          )}
        </span>
        <Button onClick={onClose}>{t("viewer.close")}</Button>
      </div>
    </SiteModal>
  );
};

export default DocumentViewer;
